import { addressElements } from '../enum/address-elements';

const STREET_FIELDS = new Set(['House Number', 'Street Name']);

export type DisplayAddressInput = {
  city?: string | null;
  city_name?: string | null;
  cityName?: string | null;
  street_name?: string | null;
  streetName?: string | null;
  street?: string | null;
  country?: string | { name?: string } | null;
  country_name?: string | null;
  countryName?: string | null;
  postal_code?: string | null;
  postalCode?: string | null;
  zip?: string | null;
  zip_code?: string | null;
  house_number?: string | null;
  streetNumber?: string | null;
  houseNumber?: string | null;
  street_number?: string | null;
  interior_number?: string | null;
  interiorNumber?: string | null;
  state?: string | { name?: string } | null;
  state_name?: string | null;
  stateName?: string | null;
  province?: string | null;
  formattedAddress?: string | null;
};

/**
 * Renders an address using the workspace profile field order (`address_format`).
 *
 * - Non-street fields keep the exact workspace order
 * - House Number + Street Name are always treated as a single unit (joined by space, never a comma),
 *   regardless of whether the number comes before or after the street name
 * - State Name + Postal Code / Zip Code are joined by a space when adjacent in the format
 * - No trailing commas
 */
export function formatDisplayAddress(
  address: DisplayAddressInput | null | undefined,
  formatOverride?: string | null,
): string {
  if (!address) {
    return '';
  }

  const format = parseAddressFormat(formatOverride);
  const values = resolveAddressValues(address);

  const streetFieldOrder = format.filter((field) => STREET_FIELDS.has(field));
  const streetToken = (streetFieldOrder.length
    ? streetFieldOrder
    : (['House Number', 'Street Name'] as const)
  )
    .map((field) => values[field])
    .filter((part): part is string => !!part)
    .join(' ');

  const tokens: { value: string; fields: Set<string> }[] = [];
  let streetEmitted = false;

  for (const field of format) {
    if (STREET_FIELDS.has(field)) {
      if (!streetEmitted && streetToken) {
        tokens.push({ value: streetToken, fields: STREET_FIELDS });
        streetEmitted = true;
      }
      continue;
    }

    const value = values[field];
    if (value) {
      tokens.push({ value, fields: new Set([field]) });
    }
  }

  if (tokens.length === 0) {
    return '';
  }

  let result = tokens[0].value;

  for (let i = 1; i < tokens.length; i++) {
    const prev = tokens[i - 1];
    const curr = tokens[i];

    const isPrevStatePostal =
      prev.fields.has('State Name') ||
      prev.fields.has('Postal Code') ||
      prev.fields.has('Zip Code');
    const isCurrStatePostal =
      curr.fields.has('State Name') ||
      curr.fields.has('Postal Code') ||
      curr.fields.has('Zip Code');
    const isAdjacentStatePostal = isPrevStatePostal && isCurrStatePostal;

    if (isAdjacentStatePostal) {
      result += ' ' + curr.value;
    } else {
      result += ', ' + curr.value;
    }
  }

  return result;
}

function parseAddressFormat(formatOverride?: string | null): string[] {
  const raw =
    (formatOverride && String(formatOverride).trim()) ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('address_format')
      : null) ||
    addressElements.join(',');

  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function resolveAddressValues(
  address: any,
): Record<string, string> {
  if (!address) return {};
  return {
    'City Name': trimPart(address.city || address.city_name || address.cityName),
    'Street Name': trimPart(address.street_name || address.streetName || address.street),
    'Country Name': resolveNamedPart(address.country || address.country_name || address.countryName),
    'Postal Code': trimPart(address.postal_code || address.postalCode || address.zip || address.zip_code),
    'Zip Code': trimPart(address.postal_code || address.postalCode || address.zip || address.zip_code),
    'House Number': trimPart(address.house_number || address.streetNumber || address.houseNumber || address.street_number),
    'Interior Number': trimPart(address.interior_number || address.interiorNumber),
    'State Name': resolveNamedPart(address.state || address.state_name || address.stateName || address.province),
  };
}

function resolveNamedPart(value: any): string {
  if (value == null || value === '') {
    return '';
  }
  if (typeof value === 'object') {
    return trimPart(value.name || value.title || value.label);
  }
  return trimPart(value);
}

function trimPart(value: any): string {
  return value != null ? String(value).trim() : '';
}
